import React, { useMemo, useState } from "react";
import { FeatherChevronDown, FeatherChevronRight } from "@subframe/core";
import * as SubframeUtils from "../../../../ui/utils";
import { ReportColumn, ReportNode } from "../../data/ReportsApiDataSource";

interface ReportTreeProps {
  root: ReportNode;
  columns: ReportColumn[];
  /** Trial balance: Debit / Credit per column instead of one Total. */
  debitCredit: boolean;
}

const inr = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Indian grouping; negatives in parentheses the way Zoho prints them. */
export const formatAmount = (n: number | undefined | null): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return "";
  const s = inr.format(Math.abs(n));
  return n < 0 ? `(${s})` : s;
};

const nodeKey = (node: ReportNode, path: string) => `${path}/${node.node_name ?? node.account_id ?? node.name}`;

/** Every group key, so the tree can start fully expanded. */
const collectGroupKeys = (node: ReportNode, path: string, out: Set<string>) => {
  const key = nodeKey(node, path);
  if (node.children.length > 0) out.add(key);
  node.children.forEach((c) => collectGroupKeys(c, key, out));
};

/**
 * Hierarchical statement table as Zoho renders it: collapsible groups, a
 * "Total for X" footer under each group, formula rows (Gross Profit, Net
 * Profit, Total Assets…) bold, amounts right-aligned.
 */
export const ReportTree: React.FC<ReportTreeProps> = ({ root, columns, debitCredit }) => {
  const allGroups = useMemo(() => {
    const s = new Set<string>();
    root.children.forEach((c) => collectGroupKeys(c, "", s));
    return s;
  }, [root]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const amountCols = debitCredit ? columns.length * 2 : columns.length;
  const rows: React.ReactNode[] = [];

  const cells = (node: ReportNode, bold: boolean) =>
    columns.map((_, ci) => {
      const v = node.values[ci] ?? {};
      const cls = SubframeUtils.twClassNames(
        "whitespace-nowrap px-4 py-1.5 text-right tabular-nums text-body font-body text-default-font",
        { "font-body-bold": bold }
      );
      return debitCredit ? (
        <React.Fragment key={ci}>
          <td className={cls}>{formatAmount(v.net_debit)}</td>
          <td className={cls}>{formatAmount(v.net_credit)}</td>
        </React.Fragment>
      ) : (
        <td key={ci} className={cls}>
          {formatAmount(v.total)}
        </td>
      );
    });

  const render = (node: ReportNode, path: string, depth: number) => {
    const key = nodeKey(node, path);
    const isGroup = node.children.length > 0;
    const isCollapsed = collapsed.has(key);
    const indent = { paddingLeft: `${16 + depth * 20}px` };

    if (node.is_formula) {
      rows.push(
        <tr key={key} className="border-t border-solid border-neutral-border bg-neutral-50">
          <td style={indent} className="py-2 pr-4 text-body-bold font-body-bold text-default-font">
            {node.total_label || node.name}
          </td>
          {cells(node, true)}
        </tr>
      );
      return;
    }

    if (!isGroup) {
      rows.push(
        <tr key={key} className="hover:bg-neutral-50">
          <td style={indent} className="py-1.5 pr-4 text-body font-body text-default-font">
            {node.name}
            {node.account_code ? (
              <span className="ml-2 text-caption font-caption text-subtext-color">{node.account_code}</span>
            ) : null}
          </td>
          {cells(node, false)}
        </tr>
      );
      return;
    }

    rows.push(
      <tr key={key} className="hover:bg-neutral-50">
        <td style={indent} className="py-1.5 pr-4">
          <button
            type="button"
            onClick={() => toggle(key)}
            aria-expanded={!isCollapsed}
            className="flex items-center gap-1 text-body-bold font-body-bold text-default-font"
          >
            {isCollapsed ? (
              <FeatherChevronRight className="text-body font-body text-subtext-color" />
            ) : (
              <FeatherChevronDown className="text-body font-body text-subtext-color" />
            )}
            {node.name}
          </button>
        </td>
        {isCollapsed ? cells(node, true) : <td colSpan={amountCols} />}
      </tr>
    );

    if (!isCollapsed) {
      node.children.forEach((c) => render(c, key, depth + 1));
      rows.push(
        <tr key={`${key}/total`} className="border-t border-solid border-neutral-border">
          <td style={indent} className="py-1.5 pr-4 text-body-bold font-body-bold text-default-font">
            {node.total_label || `Total for ${node.name}`}
          </td>
          {cells(node, true)}
        </tr>
      );
    }
  };

  root.children.forEach((c) => render(c, "", 0));

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(allGroups));

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex justify-end gap-3 text-caption font-caption">
        <button type="button" onClick={expandAll} className="text-brand-primary hover:underline">
          Expand all
        </button>
        <button type="button" onClick={collapseAll} className="text-brand-primary hover:underline">
          Collapse all
        </button>
      </div>
      <div className="w-full overflow-x-auto rounded-md border border-solid border-neutral-border">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-solid border-neutral-border bg-neutral-50">
              <th className="px-4 py-2 text-left text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color">
                Account
              </th>
              {columns.map((col, ci) =>
                debitCredit ? (
                  <React.Fragment key={ci}>
                    <th className="px-4 py-2 text-right text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color">
                      Debit
                    </th>
                    <th className="px-4 py-2 text-right text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color">
                      Credit
                    </th>
                  </React.Fragment>
                ) : (
                  <th
                    key={ci}
                    className="px-4 py-2 text-right text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color"
                  >
                    {col.label || "Total"}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={amountCols + 1} className="px-4 py-8 text-center text-body font-body text-subtext-color">
                  No transactions in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
